import { waitForPromise } from '@ember/test-waiters';
import { numberToUserDisplay } from '@ebiz/pretty-num';
import CdpService from '../services/cdp';
import ModelService from '../services/model';
import type IntlService from 'ember-intl/services/intl';
import * as AditoolsSpice from '@ebiz/aditools-spice/netlists';

interface CmsData {
  Product: {
    id: string;
    creationDate: string;
    lastUpdateDate: string;
    launchDate: string;
    product: string;
    status: string;
    url: string;
    shortDesc: string;
    longDesc: string;
    category: string;
    categoryID: string;
    subCategory: string;
    subCategoryID: string;
    Videos: Record<string, string>[] | null;
    Webcasts: Record<string, string>[] | null;
    cftls: Record<string, string>[] | null;
    diagrams: Record<string, string>[] | null;
    evalBoards: Record<string, string>[] | null;
    pressReleases: Record<string, string>[] | null;
    AlternativeParts: Record<string, string>[] | null;
    CompanionProduct: Record<string, string>[] | null;
    ProductDocs: [ProductDocs];
  };
  menuItems: Record<string, string>[];
}

interface ProductDocs {
  Features: string;
  PstMap: Record<string, string>;
  Specifications: Record<string, string>[] | null;
  Documentation: Record<string, string>[] | null;
  DeviceDrivers: Record<string, string>[] | null;
  Hardware: Record<string, string>[] | null;
  SimulationModels: [SimulationModel];
}

interface SimulationModel {
  id: string;
  dataSheetRevisionNumber: string;
  fileSize: string;
  fileURL: string;
  languageCode: string;
  UID: string;
  nodename: string;
  optionalDesc: string;
  publishDate: string;
  lastUpdatedDate: string;
  releaseDate: string;
  title: string;
  titlePlain: string;
}

const analysisList: Record<string, string> = {
  AC: 'ACAnalysis.cir',
  NOISE: 'NoiseAnalysis.cir',
  TRAN: 'TransientAnalysis.cir',
};

const INFINITY = 1e66;

export function getSchematicName(page: string, model: ModelService): string {
  let schematicName = 'Photo';
  const schematicBias = model.state.Bias === '+' ? 'Pos' : 'Neg';
  const schematicStage = model.state.StageCount === 1 || page === 'photodiode' ? '1stage' : '2stage';
  let schematicVoltageFollower = '';
  if (
    page !== 'photodiode' &&
    model.state.Bias === '-' &&
    model.schematicModel.stage2 &&
    model.schematicModel.stage2.rf === 0 &&
    model.schematicModel.stage2.rg === INFINITY
  ) {
    schematicVoltageFollower = 'VoltageFollower';
  }
  schematicName = schematicName + schematicBias + schematicStage + schematicVoltageFollower;
  if (page === 'nextsteps') {
    schematicName = schematicName + '_NextSteps';
  }
  return schematicName;
}

export async function getSpiceNetListFiles(
  pathURL: string,
  name: string,
  opamps: string[],
  downloadType: string,
  modelFiles: Record<string, string>,
  modelService: ModelService,
  intl: IntlService,
): Promise<{
  downloadFiles: Array<DownloadFile>;
  netListParts: Array<{ partName: string; type: string }>;
}> {
  const downloadFiles: Array<DownloadFile> = [];
  let downloadFile: DownloadFile;
  const listOfParts = [];
  if (checkModelFiles(modelFiles)) {
    const modelFile = getModelFile(getMacroModelInfo([...new Set(opamps)], modelFiles));
    const schematicValues = getSchematicValues(name, modelFiles, modelService);
    const responseLimits = getResponseLimits(modelService);
    [...new Set(opamps)].map((item) => {
      listOfParts.push({ partName: item, type: 'Netlist' });
    });
    for (const cir in analysisList) {
      const netlist = new AditoolsSpice.SpiceNetList(await getTemplateFile(pathURL, `${name}_${analysisList[cir]}`));
      // Update the title line
      const stageText =
        opamps.length > 1
          ? `2 stages using ${[...new Set(opamps)].join(',')}`
          : `1 stage using ${[...new Set(opamps)].join(',')}`;
      netlist.updateTitle(`Photodiode ${cir} Analysis Spice Netlist - ${stageText}`);
      // Update the input signal
      const pulseParameters: AditoolsSpice.PulseSourceParameters = {
        lowValue: 0,
        highValue: modelService.state.Ip,
        delayTime: 0.000000001,
        riseTime: 0.000000001,
        fallTime: 0.000000001,
        pulseWidth: 1,
        period: 2,
      };
      netlist.updatePulseCurrentSource('IS', pulseParameters);
      // Update the values for the component elements and stages
      for (const item in schematicValues) {
        const itemValue = schematicValues[item] as string | number;
        netlist.updateElementValue(
          item,
          typeof itemValue === 'number' ? numberToUserDisplay(itemValue, 'Spice', 3, { showZeroes: false }) : itemValue,
        );
      }
      // Update the simulation directive line
      if (cir == 'AC') {
        const parameters: AditoolsSpice.SweepParameters = {
          sweepType: 'decade',
          points: 100,
          startFrequency: responseLimits['minimumFrequency'],
          stopFrequency: responseLimits['maximumFrequency'],
        };
        netlist.updateACAnalysis(parameters);
      }
      if (cir == 'NOISE') {
        const parameters: AditoolsSpice.SweepParameters = {
          sweepType: 'decade',
          points: 100,
          startFrequency: responseLimits['minimumFrequency'],
          stopFrequency: responseLimits['maximumFrequency'],
        };
        netlist.updateNoiseAnalysis(parameters);
      }
      if (cir == 'TRAN') {
        const parameters: AditoolsSpice.TransientParameters = {
          step: 0.000000001,
          stopTime: responseLimits['maximumTimeAxis'],
          /*stopTime: responseLimits.maximumTimeAxis / 2,*/
        };
        netlist.updateTransientAnalysis(parameters);
      }
      // Push the content into the downloadFiles collection
      downloadFile = {
        name: analysisList[cir],
        value: netlist.netlist + modelFile,
        location: downloadType == 'all' ? 'SPICE Files\\Netlists' : 'Netlists',
        type: 'LTSPICE',
      };
      downloadFiles.push(downloadFile);
    }
    // Check if README.txt needs to be pushed into the downloadFiles collection
    if (downloadFiles.length > 0) {
      const fileName = intl.t('download.spice-files.message_netlist_readme_filename');
      const content = intl.t('download.spice-files.message_netlist_readme');
      downloadFiles.push({
        name: fileName,
        value: content,
        location: downloadType == 'all' ? 'SPICE Files\\Netlists' : 'Netlists',
        type: 'LTSPICE',
      });
    }
  } else {
    for (const name in modelFiles) {
      if (modelFiles[name] !== '') listOfParts.push({ partName: name, type: 'Netlist' });
    }
  }
  return { downloadFiles: downloadFiles, netListParts: listOfParts };
}

export function getModelFiles(opamps: string[], cdp: CdpService): PromiseHash {
  const fileHash: PromiseHash = {};
  for (const opamp of opamps) {
    fileHash[opamp] = getModelFileForGeneric(opamp, cdp);
  }
  return fileHash;
}

function getSchematicValues(
  name: string,
  modelFiles: Record<string, string>,
  modelService: ModelService,
): Record<string, string | number> {
  const schematicValues: Record<string, string | number> = {};
  const model = modelService;
  let netlist;

  schematicValues['Cd'] = model.state.Cd;
  schematicValues['Rsh'] = model.state.Rsh;
  schematicValues['Cf'] = model.schematicModel.stage1.cf;
  schematicValues['Rf'] = model.schematicModel.stage1.rf;
  netlist = new AditoolsSpice.SpiceNetList(modelFiles[model.schematicModel.stage1.opAmp.partNumber] as string);
  const x1 = _buildXString(netlist, 0);
  schematicValues['X1'] = x1.xString;

  if (model.schematicModel.stage2) {
    netlist = new AditoolsSpice.SpiceNetList(modelFiles[model.schematicModel.stage2.opAmp.partNumber] as string);
    const x2 = _buildXString(netlist, x1.lastFloatIndex);
    schematicValues['X2'] = x2.xString;
    schematicValues['Cfilt'] = model.schematicModel.stage2.cfilter;
    if (name == 'photoneg2stagevoltagefollower') {
      schematicValues['Rfilt'] = model.schematicModel.stage2.rfilter;
    }
    if (name == 'photoneg2stage') {
      schematicValues['Rfilt'] = model.schematicModel.stage2.rfilter;
      schematicValues['Rf2'] = model.schematicModel.stage2.rf;
      schematicValues['Rg2'] = model.schematicModel.stage2.rg;
    }
    if (name == 'photopos2stage') {
      schematicValues['Rf2'] = model.schematicModel.stage2.rf;
      schematicValues['Rg2'] = model.schematicModel.stage2.rg;
    }
  }
  schematicValues['VP'] = numberToUserDisplay(model.powerSupplyRange.max, 'Spice', 5, { showZeroes: false });
  schematicValues['VM'] = numberToUserDisplay(-model.powerSupplyRange.max, 'Spice', 5, { showZeroes: false });

  return schematicValues;
}

function getResponseLimits(modelService: ModelService): Record<string, number> {
  return {
    minimumFrequency: modelService.frequencyResponseVA.graph.limits.xmin,
    maximumFrequency: modelService.frequencyResponseVA.graph.limits.xmax,
    minimumTimeAxis: modelService.pulseResponse.graph.limits.xmin,
    maximumTimeAxis: modelService.pulseResponse.graph.limits.xmax,
  };
}

function getTemplateFile(pathURL: string, name: string): Promise<string> {
  const url = `${pathURL}/${name}`;
  return waitForPromise(
    fetch(url).then((response) => {
      if (response.status !== 200) {
        throw `HTTP Request Failed reading ${url}: ${response.status} ${response.statusText}`;
      }
      return response.text();
    }),
  );
}

function getModelFileForGeneric(partNumber: string, cdp: CdpService): Promise<string> {
  return cdp.getCmsData(partNumber.toLowerCase()).then((data) => {
    if (!data) return '';
    const json: SimulationModel = (data as CmsData)['Product']['ProductDocs'][0]['SimulationModels'][0];
    let fileName = '';
    if (typeof json !== 'undefined' && json) {
      fileName = json['fileURL'];
      fileName = fileName.substring(fileName.indexOf('=') + 1);
      fileName = _proxyIfLocal(fileName);
      const url = `${fileName}?modeltype=spice-models`;
      return fetch(url)
        .then((response) => {
          if (response.status !== 200) {
            throw `HTTP Request Failed fetching ${url}: ${response.status} ${response.statusText}`;
          }
          return response.text();
        })
        .then((text) => {
          if (text.indexOf(`Page Not Found`) !== -1) {
            console.log('Unexpected: 200 response status with embedded "Page Not Found" for CIR file - url: ', url);
            throw `HTTP Request Succeeded but contained Page Not Found - url: ${url}`;
          }
          return text;
        })
        .catch(() => {
          return '';
        });
    } else {
      console.log(`SPICE model doesn't exist for ${partNumber}`);
      return '';
    }
  });
}

function getMacroModelInfo(
  opamps: string[],
  modelFiles: Record<string, string>,
): Record<string, Record<string, string>> {
  const result: Record<string, Record<string, string>> = {};
  opamps.forEach((opamp) => {
    const netlist = new AditoolsSpice.SpiceNetList(modelFiles[opamp] as string);
    const subcktName = modelFiles[opamp] !== '' ? netlist.getSubcircuitName() : opamp;
    result[opamp] = {
      subcktName: subcktName,
      data: modelFiles[opamp] as string,
    };
  });
  return result;
}

function getModelFile(macroModelInfo: Record<string, Record<string, string>>): string {
  let result = '';
  const modelFiles: Record<string, string> = {};
  for (const opamp in macroModelInfo) {
    const macroModelInfoOpamp = macroModelInfo[opamp] as Record<string, string>;
    modelFiles[macroModelInfoOpamp['subcktName'] as string] = macroModelInfoOpamp['data'] as string;
  }
  for (const name in modelFiles) {
    let modelFile = modelFiles[name];
    let crlf = '';
    if (modelFile?.endsWith('\n')) {
      if (!modelFile.endsWith('\r\n')) {
        modelFile = modelFile.slice(0, -1);
        crlf = '\r\n';
      }
    } else {
      crlf = '\r\n';
    }
    result += modelFile + crlf;
  }
  return result;
}

function _proxyIfLocal(url: string): string {
  return window.location.hostname === 'localhost' ? url.substring(url.search(/[^/][/][^/]/) + 1) : url;
}

function checkModelFiles(modelFiles: Record<string, string>): boolean {
  const isValid = true;
  for (const name in modelFiles) {
    if (modelFiles[name] === '') return false;
  }
  return isValid;
}

function _buildXString(
  netlist: AditoolsSpice.SpiceNetList,
  lastFloatIndex: number,
): { xString: string; lastFloatIndex: number } {
  const DEFAULT_PIN_COUNT = 5;
  const extraPins = netlist.getSubcircuitPortCount('.subckt') - DEFAULT_PIN_COUNT;
  const x1Items = [];
  for (let i = 0; i < extraPins; i++) {
    x1Items.push('PDFloat' + (++lastFloatIndex).toString());
  }
  x1Items.push(netlist.getSubcircuitName());
  return { xString: x1Items.join(' '), lastFloatIndex: lastFloatIndex };
}
